import { fieldSurface } from '../field-focus'
import { labelContent } from './labelContent'
import { useId, useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectSection {
  title?: string
  options: SelectOption[]
}

interface SelectProps {
  label?: string
  /** Accessible name when there is no visible label. Falls back to the placeholder. */
  ariaLabel?: string
  /** Id of an element that describes the control, such as a hint under it. */
  ariaDescribedBy?: string
  size?: 'large' | 'small'
  options?: SelectOption[]
  sections?: SelectSection[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  error?: string
  disabled?: boolean
  className?: string
}

const SIZE: Record<'large' | 'small', { height: number; px: number }> = {
  large: { height: 40, px: 12 },
  small: { height: 32, px: 10 },
}

export function Select({
  label,
  size = 'large',
  options,
  sections,
  value,
  onChange,
  placeholder = 'Select…',
  error,
  disabled = false,
  className = '',
  ariaLabel,
  ariaDescribedBy,
}: SelectProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const id = useId()
  const labelId = `${id}-label`

  const normalizedSections: SelectSection[] = sections ?? (options ? [{ options }] : [])
  const allOptions = normalizedSections.flatMap(s => s.options)
  const selected = allOptions.find(o => o.value === value)

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  const { borderColor, backgroundColor } = fieldSurface({ focused: open, error: Boolean(error) })
  const { height, px } = SIZE[size]

  return (
    <div className={`flex flex-col ${className}`} style={{ gap: 8 }}>
      {label && (
        <label id={labelId} htmlFor={id} className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
          {labelContent(label)}
        </label>
      )}

      <div ref={containerRef} className="relative">
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          {...(label ? { 'aria-labelledby': labelId } : { 'aria-label': ariaLabel ?? placeholder })}
          {...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {})}
          disabled={disabled}
          onClick={() => !disabled && setOpen(o => !o)}
          className="w-full flex items-center justify-between font-jakarta"
          style={{
            height,
            backgroundColor,
            border: `0.5px solid ${borderColor}`,
            paddingLeft: px,
            paddingRight: px,
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.15s ease',
          }}
        >
          <span
            className={`min-w-0 truncate ${selected ? 'text-text-input' : 'text-text-description'}`}
            style={{ fontSize: 12, lineHeight: '16px', letterSpacing: '-0.12px' }}
            // An option longer than the control used to push the chevron out through the
            // border rather than clip, so a narrow select read as broken.
            title={selected ? selected.label : undefined}
          >
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown
            size={14}
            className={`flex-shrink-0 text-text-support transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {open && (
          <div
            className="absolute left-0 right-0 z-50 overflow-y-auto"
            style={{
              top: height + 4,
              backgroundColor: '#1A1D21',
              border: '0.5px solid #4B5563',
              paddingTop: 8,
              paddingBottom: 8,
              maxHeight: 240,
            }}
          >
            {normalizedSections.map((section, si) => (
              <div key={si} style={{ marginBottom: si < normalizedSections.length - 1 ? 10 : 0 }}>
                {section.title && (
                  <div
                    className="font-jakarta text-text-support"
                    style={{ fontSize: 10, lineHeight: '12px', letterSpacing: '0.2px', padding: '4px 12px 6px' }}
                  >
                    {section.title}
                  </div>
                )}
                {section.options.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { onChange(opt.value); setOpen(false) }}
                    className={`w-full text-left font-mulish hover:bg-background-skeleton transition-colors ${opt.value === value ? 'text-white' : 'text-text-support'}`}
                    style={{ fontSize: 12, lineHeight: '16px', padding: '8px 12px' }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <span className="font-mulish" style={{ fontSize: 10, lineHeight: '12px', color: '#F05042' }}>
          {error}
        </span>
      )}
    </div>
  )
}
